import { useState } from "react";

interface AccommodationApp {
  id: string;
  businessName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  district: string;
  businessType: string;
  priceRange: string;
  roomCount?: string;
  starRating?: string;
  shortDesc: string;
  description: string;
  amenities?: string[];
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
  notified?: boolean;
  adminNote?: string;
}

interface Props {
  showToast: (msg: string, type: "success" | "error" | "info") => void;
}

const AccommodationSection = ({ showToast }: Props) => {
  const [accommodationApps, setAccommodationApps] = useState<AccommodationApp[]>(() =>
    JSON.parse(localStorage.getItem("pendingAccommodations") || "[]")
  );
  const [selectedAccApp, setSelectedAccApp] = useState<AccommodationApp | null>(null);
  const [accEmailSending, setAccEmailSending] = useState<"approve" | "reject" | null>(null);
  const [accNoteEdit, setAccNoteEdit] = useState("");
  const [showAccNoteEdit, setShowAccNoteEdit] = useState(false);
  const [accFilter, setAccFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [selectedAccIds, setSelectedAccIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [accSearch, setAccSearch] = useState("");
  const [accCityFilter, setAccCityFilter] = useState("all");
  const [accTypeFilter, setAccTypeFilter] = useState("all");
  const [accPriceFilter, setAccPriceFilter] = useState("all");

  const accCities = Array.from(new Set(accommodationApps.map((a) => a.city))).filter(Boolean).sort();
  const accTypes = Array.from(new Set(accommodationApps.map((a) => a.businessType))).filter(Boolean).sort();
  const accPrices = Array.from(new Set(accommodationApps.map((a) => a.priceRange))).filter(Boolean).sort();

  const TYPE_LABELS: Record<string, string> = {
    hotel: "Otel", pansiyon: "Pansiyon", butik: "Butik Otel",
    apart: "Apart", kamp: "Kamp & Glamping", firma: "Turizm Firması",
  };

  const filteredAccApps = accommodationApps
    .filter((a) => accFilter === "all" || a.status === accFilter)
    .filter((a) => {
      if (!accSearch.trim()) return true;
      const q = accSearch.toLowerCase();
      return (
        a.businessName.toLowerCase().includes(q) ||
        a.ownerName.toLowerCase().includes(q) ||
        a.city.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q)
      );
    })
    .filter((a) => accCityFilter === "all" || a.city === accCityFilter)
    .filter((a) => accTypeFilter === "all" || a.businessType === accTypeFilter)
    .filter((a) => accPriceFilter === "all" || a.priceRange === accPriceFilter);

  const pendingAccApps = filteredAccApps.filter((a) => a.status === "pending");

  const handleAccApprove = (app: AccommodationApp) => {
    const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
    const updated = all.map((a: AccommodationApp) => a.id === app.id ? { ...a, status: "approved" } : a);
    localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
    setAccommodationApps(updated);
    setSelectedAccApp({ ...app, status: "approved" });
    showToast(`"${app.businessName}" onaylandı!`, "success");
    setTimeout(() => {
      const subject = `Tebrikler! "${app.businessName}" Başvurunuz Onaylandı`;
      const body = `Sayın ${app.ownerName},\n\nİşletmeniz "${app.businessName}" onaylandı ve Konaklama Rehberi'nde yayınlandı.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;
      window.open(`mailto:${app.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    }, 600);
  };

  const handleAccReject = (app: AccommodationApp) => {
    const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
    const updated = all.map((a: AccommodationApp) => a.id === app.id ? { ...a, status: "rejected" } : a);
    localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
    setAccommodationApps(updated);
    setSelectedAccApp({ ...app, status: "rejected" });
    showToast(`"${app.businessName}" reddedildi.`, "error");
    setTimeout(() => {
      const subject = `"${app.businessName}" Başvurunuz Hakkında Bilgi`;
      const body = `Sayın ${app.ownerName},\n\nBaşvurunuzu inceledik, ancak şu an için onaylayamıyoruz. Daha fazla bilgi için iletişime geçebilirsiniz.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;
      window.open(`mailto:${app.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
    }, 600);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h2 className="text-lg font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Otel &amp; Pansiyon Başvuruları
        </h2>
        <span className="text-sm text-stone-400">{accommodationApps.length} toplam başvuru</span>
      </div>

      {/* Arama + Filtreler */}
      <div className="bg-white rounded-xl border border-stone-100 p-4 mb-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-48 flex items-center bg-stone-50 border border-stone-200 rounded-lg px-3 gap-2 focus-within:border-amber-400 transition-colors">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-search-line text-stone-400 text-sm"></i>
            </div>
            <input
              type="text"
              value={accSearch}
              onChange={(e) => setAccSearch(e.target.value)}
              placeholder="İşletme, sahip, şehir veya e-posta ara..."
              className="flex-1 bg-transparent text-sm text-stone-700 outline-none py-2"
            />
            {accSearch && (
              <button onClick={() => setAccSearch("")} className="w-4 h-4 flex items-center justify-center text-stone-400 hover:text-stone-600 cursor-pointer">
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
          {(accSearch || accCityFilter !== "all" || accTypeFilter !== "all" || accPriceFilter !== "all") && (
            <button
              onClick={() => { setAccSearch(""); setAccCityFilter("all"); setAccTypeFilter("all"); setAccPriceFilter("all"); }}
              className="flex items-center gap-1 text-xs px-3 py-2 bg-stone-100 text-stone-600 rounded-lg hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line"></i> Temizle
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={accCityFilter} onChange={(e) => setAccCityFilter(e.target.value)} className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer">
            <option value="all">Tüm Şehirler</option>
            {accCities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={accTypeFilter} onChange={(e) => setAccTypeFilter(e.target.value)} className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer">
            <option value="all">Tüm Türler</option>
            {accTypes.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
          <select value={accPriceFilter} onChange={(e) => setAccPriceFilter(e.target.value)} className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none focus:border-amber-400 bg-white cursor-pointer">
            <option value="all">Tüm Fiyatlar</option>
            {accPrices.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <p className="text-xs text-stone-400 ml-auto">{filteredAccApps.length} sonuç</p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {(["all", "pending", "approved", "rejected"] as const).map((f) => {
          const cnt = f === "all" ? accommodationApps.length : accommodationApps.filter((a) => a.status === f).length;
          return (
            <button
              key={f}
              onClick={() => { setAccFilter(f); setSelectedAccIds(new Set()); }}
              className={`text-xs px-4 py-2 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                accFilter === f ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-600 hover:border-stone-300"
              }`}
            >
              {f === "all" ? "Tümü" : f === "pending" ? "Bekleyenler" : f === "approved" ? "Onaylananlar" : "Reddedilenler"}
              {cnt > 0 && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  accFilter === f ? "bg-white/20 text-white" : f === "pending" ? "bg-amber-100 text-amber-700" : f === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"
                }`}>{cnt}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Bulk Action Bar */}
      {selectedAccIds.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full">
              <i className="ri-checkbox-multiple-line text-white text-xs"></i>
            </div>
            <span className="text-sm font-semibold text-amber-800">{selectedAccIds.size} başvuru seçildi</span>
          </div>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <button
              disabled={bulkProcessing}
              onClick={() => {
                setBulkProcessing(true);
                const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
                const updated = all.map((a: AccommodationApp) =>
                  selectedAccIds.has(a.id) && a.status === "pending" ? { ...a, status: "approved" } : a
                );
                localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
                setAccommodationApps(updated);
                setSelectedAccIds(new Set());
                setBulkProcessing(false);
                showToast(`${selectedAccIds.size} başvuru toplu olarak onaylandı!`, "success");
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {bulkProcessing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-checkbox-circle-line"></i>}
              Tümünü Onayla
            </button>
            <button
              disabled={bulkProcessing}
              onClick={() => {
                setBulkProcessing(true);
                const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
                const updated = all.map((a: AccommodationApp) =>
                  selectedAccIds.has(a.id) && a.status === "pending" ? { ...a, status: "rejected" } : a
                );
                localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
                setAccommodationApps(updated);
                setSelectedAccIds(new Set());
                setBulkProcessing(false);
                showToast(`${selectedAccIds.size} başvuru reddedildi.`, "error");
              }}
              className="flex items-center gap-1.5 px-4 py-2 bg-rose-500 hover:bg-rose-600 disabled:opacity-50 text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line"></i> Tümünü Reddet
            </button>
            <button
              onClick={() => setSelectedAccIds(new Set())}
              className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 hover:bg-stone-200 text-stone-600 text-xs font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line"></i> Seçimi Kaldır
            </button>
          </div>
        </div>
      )}

      {accommodationApps.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-stone-100">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="ri-hotel-line text-stone-300 text-4xl"></i>
          </div>
          <p className="text-stone-400 text-sm">Henüz konaklama başvurusu yok.</p>
          <p className="text-stone-300 text-xs mt-2">Yeni başvurular burada görünecek.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-2">
            {pendingAccApps.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 rounded-xl border border-stone-100 mb-1">
                <button
                  onClick={() => {
                    const pendingIds = pendingAccApps.map((a) => a.id);
                    const allSelected = pendingIds.every((id) => selectedAccIds.has(id));
                    if (allSelected) {
                      setSelectedAccIds(new Set());
                    } else {
                      setSelectedAccIds(new Set(pendingIds));
                    }
                  }}
                  className={`w-4 h-4 flex items-center justify-center rounded border-2 flex-shrink-0 transition-all cursor-pointer ${
                    pendingAccApps.every((a) => selectedAccIds.has(a.id))
                      ? "bg-amber-500 border-amber-500"
                      : "border-stone-300 hover:border-amber-400"
                  }`}
                >
                  {pendingAccApps.every((a) => selectedAccIds.has(a.id)) && (
                    <i className="ri-check-line text-white text-xs"></i>
                  )}
                </button>
                <span className="text-xs text-stone-500 font-medium">
                  Bekleyen {pendingAccApps.length} başvuruyu seç
                </span>
              </div>
            )}

            {filteredAccApps.length === 0 ? (
              <div className="text-center py-10 bg-white rounded-xl border border-stone-100">
                <p className="text-stone-400 text-sm">Bu filtreye uygun başvuru yok</p>
              </div>
            ) : filteredAccApps.map((app) => {
              const isChecked = selectedAccIds.has(app.id);
              return (
                <div
                  key={app.id}
                  className={`relative w-full text-left p-4 rounded-xl border transition-all ${
                    selectedAccApp?.id === app.id ? "border-amber-400 bg-amber-50" : isChecked ? "border-amber-300 bg-amber-50/50" : "border-stone-100 bg-white hover:border-amber-200"
                  }`}
                >
                  {app.status === "pending" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAccIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(app.id)) next.delete(app.id);
                          else next.add(app.id);
                          return next;
                        });
                      }}
                      className={`absolute top-3 left-3 w-4 h-4 flex items-center justify-center rounded border-2 flex-shrink-0 transition-all cursor-pointer z-10 ${
                        isChecked ? "bg-amber-500 border-amber-500" : "border-stone-300 bg-white hover:border-amber-400"
                      }`}
                    >
                      {isChecked && <i className="ri-check-line text-white text-xs"></i>}
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedAccApp(app)}
                    className={`w-full text-left ${app.status === "pending" ? "pl-6" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-stone-900 text-sm">{app.businessName}</h3>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {app.status === "pending" && <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">Bekliyor</span>}
                        {app.status === "approved" && <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full font-medium">Onaylandı</span>}
                        {app.status === "rejected" && <span className="bg-rose-100 text-rose-700 text-xs px-2 py-0.5 rounded-full font-medium">Reddedildi</span>}
                      </div>
                    </div>
                    <p className="text-xs text-stone-500">{app.city} · {app.businessType}</p>
                    <p className="text-xs text-stone-400 mt-1">{new Date(app.appliedAt).toLocaleDateString("tr-TR")}</p>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="lg:col-span-2">
            {selectedAccApp ? (
              <div className="bg-white rounded-2xl border border-stone-100 p-6">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h3 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>{selectedAccApp.businessName}</h3>
                    <p className="text-stone-400 text-sm mt-0.5">{selectedAccApp.city} · {selectedAccApp.district}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    {selectedAccApp.status === "pending" && <span className="bg-amber-100 text-amber-700 text-xs px-3 py-1 rounded-full font-medium">Bekliyor</span>}
                    {selectedAccApp.status === "approved" && <span className="bg-emerald-100 text-emerald-700 text-xs px-3 py-1 rounded-full font-medium">Onaylandı</span>}
                    {selectedAccApp.status === "rejected" && <span className="bg-rose-100 text-rose-700 text-xs px-3 py-1 rounded-full font-medium">Reddedildi</span>}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-5">
                  {[
                    { label: "Yetkili", value: selectedAccApp.ownerName },
                    { label: "E-posta", value: selectedAccApp.email },
                    { label: "Telefon", value: selectedAccApp.phone },
                    { label: "Fiyat Aralığı", value: selectedAccApp.priceRange },
                    { label: "Oda Sayısı", value: selectedAccApp.roomCount || "—" },
                    { label: "Yıldız", value: selectedAccApp.starRating ? `${selectedAccApp.starRating} Yıldız` : "—" },
                  ].map((item) => (
                    <div key={item.label} className="bg-stone-50 rounded-lg p-3">
                      <p className="text-xs text-stone-400 mb-0.5">{item.label}</p>
                      <p className="text-sm font-semibold text-stone-800 truncate">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <p className="text-xs text-stone-400 mb-1">Kısa Tanıtım</p>
                  <p className="text-sm text-stone-700">{selectedAccApp.shortDesc}</p>
                </div>
                <div className="mb-4">
                  <p className="text-xs text-stone-400 mb-1">Detaylı Açıklama</p>
                  <p className="text-sm text-stone-600 leading-relaxed">{selectedAccApp.description}</p>
                </div>

                {selectedAccApp.amenities && selectedAccApp.amenities.length > 0 && (
                  <div className="mb-5">
                    <p className="text-xs text-stone-400 mb-2">Olanaklar</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedAccApp.amenities.map((a: string) => (
                        <span key={a} className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full">{a}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Admin Notu */}
                <div className="mb-4 p-4 bg-stone-50 rounded-xl border border-stone-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-sticky-note-line text-stone-500 text-sm"></i>
                      </div>
                      <p className="text-xs font-semibold text-stone-700">Admin Notu</p>
                    </div>
                    <button
                      onClick={() => { setShowAccNoteEdit((v) => !v); setAccNoteEdit(selectedAccApp.adminNote || ""); }}
                      className="text-xs text-amber-600 hover:text-amber-700 cursor-pointer whitespace-nowrap"
                    >
                      {showAccNoteEdit ? "İptal" : "Düzenle"}
                    </button>
                  </div>
                  {showAccNoteEdit ? (
                    <div>
                      <textarea
                        value={accNoteEdit}
                        onChange={(e) => setAccNoteEdit(e.target.value)}
                        rows={3}
                        maxLength={300}
                        placeholder="Bu başvuru hakkında iç not ekleyin..."
                        className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none bg-white"
                      />
                      <div className="flex items-center justify-between mt-1.5">
                        <p className="text-xs text-stone-400">{accNoteEdit.length}/300</p>
                        <button
                          onClick={() => {
                            const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
                            const updated = all.map((a: AccommodationApp) => a.id === selectedAccApp.id ? { ...a, adminNote: accNoteEdit } : a);
                            localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
                            setAccommodationApps(updated);
                            setSelectedAccApp((prev) => prev ? { ...prev, adminNote: accNoteEdit } : prev);
                            setShowAccNoteEdit(false);
                            showToast("Not kaydedildi.", "success");
                          }}
                          className="px-4 py-1.5 bg-stone-900 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-stone-700 transition-colors"
                        >
                          Kaydet
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-500 min-h-[40px]">
                      {selectedAccApp.adminNote || <span className="text-stone-300 italic text-xs">Not eklenmemiş — Düzenle&apos;ye tıklayın</span>}
                    </p>
                  )}
                </div>

                {/* Email Notification */}
                <div className="mb-5 p-4 bg-stone-50 rounded-xl border border-stone-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line text-stone-600 text-sm"></i>
                    </div>
                    <p className="text-xs font-semibold text-stone-700">E-posta Bildirimi</p>
                    {selectedAccApp.notified && (
                      <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                        <i className="ri-check-line text-xs"></i> Bildirim Gönderildi
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-500 mb-3">
                    Başvuru sahibine <strong>{selectedAccApp.email}</strong> adresine bildirim gönderin.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        setAccEmailSending("approve");
                        setTimeout(() => {
                          const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
                          const updated = all.map((a: AccommodationApp) => a.id === selectedAccApp.id ? { ...a, notified: true } : a);
                          localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
                          setAccommodationApps(updated);
                          setSelectedAccApp((prev) => prev ? { ...prev, notified: true } : prev);
                          setAccEmailSending(null);
                          const subject = `Tebrikler! "${selectedAccApp.businessName}" Başvurunuz Onaylandı`;
                          const body = `Sayın ${selectedAccApp.ownerName},\n\nİşletmeniz "${selectedAccApp.businessName}" onaylandı.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;
                          window.open(`mailto:${selectedAccApp.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
                          showToast(`Onay e-postası taslağı hazırlandı — ${selectedAccApp.email}`, "info");
                        }, 800);
                      }}
                      disabled={accEmailSending !== null}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {accEmailSending === "approve"
                        ? <><i className="ri-loader-4-line animate-spin"></i> Hazırlanıyor...</>
                        : <><i className="ri-mail-check-line"></i> Onay E-postası Gönder</>}
                    </button>
                    <button
                      onClick={() => {
                        setAccEmailSending("reject");
                        setTimeout(() => {
                          const all = JSON.parse(localStorage.getItem("pendingAccommodations") || "[]");
                          const updated = all.map((a: AccommodationApp) => a.id === selectedAccApp.id ? { ...a, notified: true } : a);
                          localStorage.setItem("pendingAccommodations", JSON.stringify(updated));
                          setAccommodationApps(updated);
                          setSelectedAccApp((prev) => prev ? { ...prev, notified: true } : prev);
                          setAccEmailSending(null);
                          const subject = `"${selectedAccApp.businessName}" Başvurunuz Hakkında Bilgi`;
                          const body = `Sayın ${selectedAccApp.ownerName},\n\nBaşvurunuzu inceledik, ancak şu an için onaylayamıyoruz.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;
                          window.open(`mailto:${selectedAccApp.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, "_blank");
                          showToast(`Red e-postası taslağı hazırlandı — ${selectedAccApp.email}`, "info");
                        }, 800);
                      }}
                      disabled={accEmailSending !== null}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {accEmailSending === "reject"
                        ? <><i className="ri-loader-4-line animate-spin"></i> Hazırlanıyor...</>
                        : <><i className="ri-mail-close-line"></i> Red E-postası Gönder</>}
                    </button>
                  </div>
                </div>

                {selectedAccApp.status === "pending" && (
                  <div className="flex gap-3 pt-4 border-t border-stone-100">
                    <button
                      onClick={() => handleAccApprove(selectedAccApp)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-checkbox-circle-line"></i> Onayla &amp; Yayınla
                    </button>
                    <button
                      onClick={() => handleAccReject(selectedAccApp)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-medium hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-circle-line"></i> Reddet
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-100 h-64 flex flex-col items-center justify-center">
                <div className="w-12 h-12 flex items-center justify-center mb-3">
                  <i className="ri-hotel-line text-stone-300 text-3xl"></i>
                </div>
                <p className="text-stone-400 text-sm">Detayları görmek için bir başvuru seçin</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccommodationSection;
